import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { EmergencyAnswerAction, EmergencyAnswerGetter } from '@/store/emergencyAnswer/emergencyAnswer.module';
import { EmergencyAnswerAttachmentSchema } from '@/store/emergencyAnswer/emergencyAnswer.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'EmergencyAnswerAttachmentFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    emergencyId: {
      type: Number,
    },
    emergencyAnswerId: {
      type: Number,
    },
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const attachmentForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      emergencyId?: number | null;
      emergencyAnswerId?: number | null;
      attachmentTypeId: number;
      name?: string | null;
      file?: string | null;
    }
    const formData = reactive<Form>({
      emergencyId: props.emergencyId,
      emergencyAnswerId: props.emergencyAnswerId,
      attachmentTypeId: 1,
      name: '',
      file: ''
    });
    const validator = computed(() => EmergencyAnswerAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file'))
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed<boolean>(() => store.getters[EmergencyAnswerGetter.STATUS] === 'init' || store.getters[EmergencyAnswerGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      Promise.resolve(formData.emergencyAnswerId = props.emergencyAnswerId)
        .then(() => attachmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      attachmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.attachmentTypeId = 1;
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(EmergencyAnswerAction.ADD_ATTACHMENTS, {
          emergencyId: formData.emergencyId,
          id: formData.emergencyAnswerId,
          attachmentTypeId: formData.attachmentTypeId,
          names: [formData.name],
          attachments: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden', props.emergencyAnswerId);
    };

    return {
      formData,
      attachmentForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  },
});
